(function () {
  'use strict';
  if (window.FotoMasterLeadSubmit) return;

  // Contact-only adapter. Book Demo intentionally retains its different EdgeTag
  // normalization. Both contact arms use the server-confirmed sales-only bridge.
  async function post(state, submitEvent) {
    const form = state.form;
    state.observePhase?.('attribution');
    const utm = await window.getUTMData();
    let context = {
      page_context: { path: window.location.pathname, title: document.title, page_type: '', resource_handle: '', product_title: '', product_handle: '' },
      product_resolution: state.productSelection.resolve(form.product),
      journey: { consent_status: 'not_granted', captured_at: new Date().toISOString(), total_visible_ms: 0, total_page_views: 0, truncated: false, pages: [] }
    };
    state.observePhase?.('context');
    try {
      if (window.FotoMasterLeadJourney) {
        context = await window.FotoMasterLeadJourney.buildSubmissionContext(form.product, state.product_catalog, context.product_resolution);
      }
    } catch (_) { /* Keep the existing consent-safe, current-page fallback. */ }
    state.observePhase?.('payload');
    const mirror = window.FotoMasterMirrorPurchase;
    const payload = { customer: {
      form_id: state.form_id, form_type: 'contact', email: form.email,
      first_name: form.first_name, last_name: form.last_name, phone: form.phone,
      tags: 'lead', country: form.country, stage: 'Done', session_id: state.session_id,
      raw_data: JSON.stringify({
        form_data: mirror ? mirror.formData(form) : form,
        tracking_diagnostics: window.FotoMasterLeadTrackingDiagnostics?.snapshot(utm)
          || '{"version":1,"phase":"before_intake","status":"unavailable"}',
        mirror_purchase_choice: mirror ? mirror.getContext(form.product) : null,
        utm_data: utm, ...context,
        lead_protection: state.leadProtection ? state.leadProtection.buildEvidence(submitEvent)
          : { client_version: 'unavailable', form_type: 'contact', challenge_status: 'unavailable' }
      })
    }};
    state.observePhase?.('request');
    state.diagnose?.('request_sent', { processing_stage: 'request' });
    let response;
    try {
      response = await fetch('https://command.fotomaster.com/shopify/shopify_customer.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
    } catch (cause) {
      const error = new Error('Lead submission failed');
      error.formDiagnosticCode = 'network';
      throw error;
    }
    state.observePhase?.('response');
    let result;
    try { result = JSON.parse(await response.text()); } catch (_) { result = {}; }
    if (!response.ok || result.status !== 'success') {
      const error = new Error('Lead submission failed');
      error.formDiagnosticCode = !response.ok ? 'http' : 'invalid_response';
      error.formDiagnosticStatus = response.status;
      throw error;
    }
    state.diagnose?.('server_response', { processing_stage: 'response', submission_result: outcome(result) });
    return result;
  }

  function outcome(result) {
    if (result.duplicate) return 'duplicate';
    if (result.accepted !== true) return 'acknowledged_not_accepted';
    return result.sales_eligible === true && result.request_classification === 'sales' ? 'accepted_sales'
      : result.request_classification === 'support' ? 'accepted_support' : 'accepted_unclassified';
  }

  function trackable(result) {
    return result.accepted === true && result.should_track !== false && !result.duplicate
      && result.sales_eligible === true && result.request_classification === 'sales';
  }

  async function finish(state, result) {
    const form = state.form;
    const accepted = trackable(result);
    const capturedAtMs = window.signalportSDK?.captureTimeMs?.() ?? Date.now();
    let allowed = false;
    try { allowed = window.ShopifyConsent ? await window.ShopifyConsent.canTrack() : false; } catch (_) {}
    if (!state._edgeTagDataSent && accepted && allowed && typeof window.edgetag === 'function') {
      try {
        const country = state.country_data.find(c => c.name === form.country);
        const identity = {
          email: form.email, phone: form.phone, firstName: form.first_name, lastName: form.last_name,
          country: country && country.code2 ? country.code2.toLowerCase() : String(form.country || '').toLowerCase(),
          state: form.state, company: form.company, product: form.product, message: form.message,
          form_id: state.form_id, session_id: state.session_id, lead_form: 'contact',
          ...(window.FotoMasterMirrorPurchase ? window.FotoMasterMirrorPurchase.eventContext(form.product) : {})
        };
        const pruned = Object.fromEntries(Object.entries(identity).filter(([, v]) =>
          v !== null && v !== undefined && !(typeof v === 'string' && v.trim() === '')));
        window.edgetag('data', pruned, undefined, { method: 'beacon' });
        state._edgeTagDataSent = true;
      } catch (_) { /* Tracking errors must not lose an accepted inquiry. */ }
    }
    if (!state._signalportIdentitySent && accepted && window.SignalportFotoMaster
        && typeof window.SignalportFotoMaster.acceptedContactAndWait === 'function') {
      try {
        const leadInput = Object.freeze({
          salesEligible: result.sales_eligible, requestClassification: result.request_classification,
          submissionId: state.form_id, formId: 'contact', email: form.email, phone: form.phone,
          firstName: form.first_name, lastName: form.last_name, country: form.country,
          state: form.state, product: form.product, capturedAtMs
        });
        let handoffDecision = null;
        try {
          handoffDecision = window.SignalportFotoMaster.prepareLeadHandoff?.(leadInput) || null;
        } catch (_) { /* Preserve ordinary capture if ownership cannot be established. */ }
        const queued = await window.SignalportFotoMaster.acceptedContactAndWait({
          ...leadInput, handoffDecision
        });
        if (queued) state._signalportIdentitySent = true;
      } catch (_) {}
    }
    // Keep the existing bridge wait and order. Queued does not mean delivered.
    window.FotoMasterFormIdentity?.complete('contact', state.form_id);
    try {
      sessionStorage.setItem('fm_lead_confirmation_v2', JSON.stringify({
        form: 'contact', contact_preference: form.contact_preference, at: Date.now()
      }));
    } catch (_) {}
    state.currentStep = 'Done';
    state.diagnose?.('complete', { submission_result: outcome(result) });
    if (result.accepted === true && !result.duplicate) {
      const detail = { request_classification: result.request_classification || 'unclassified', sales_eligible: accepted };
      state.emit('request_submit', detail);
      if (accepted) state.emit('submit', detail);
      else state.emit(result.request_classification === 'support' ? 'support_submit' : 'unclassified_submit', detail);
    }
    if (form.email_marketing_requested === true && result.accepted === true
        && !result.duplicate && result.email_signup?.managed !== true) {
      state.emailSignupPending = true;
      await state.$nextTick();
      window.FotoMasterLeadEmailOptIn?.submit(state.config.signupId, form, result);
      return;
    }
    window.location.href = '/pages/thank-you';
  }

  window.FotoMasterLeadSubmit = Object.freeze({ post, finish, trackable });
})();
